import { html, css, LitElement } from 'lit-element';
import { db, storage } from './intrivity_app.js'; // Ensure this exports your Firestore instance
import { ref, getDownloadURL } from 'firebase/storage';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  query,
  where,
  collectionGroup,
  getDoc,
  deleteDoc,
  addDoc,
  writeBatch,
} from 'firebase/firestore';

import { 
    emitEvent, loadJSONFromStorage, getJSONFromStorage, getScore, hasRole,
    INTRIVITY_LOAD_RESOURCES, INTRIVITY_LOAD_CLIENTS, INTRIVITY_LOAD_GROUPS, 
    INTRIVITY_GROUP_REPORTS_LOADED, INTRIVITY_GOOGLE_LOGGED_IN, 
    INTRIVITY_GOOGLE_LOGGED_OUT, INTRIVITY_LOGIN, INTRIVITY_LOGGED_IN, 
    INTRIVITY_LOGOUT, INTRIVITY_NAV_SELECT, INTRIVITY_PRINTABLE_TOGGLE, 
    INTRIVITY_GROUP_SELECT, INTRIVITY_MASQUERADE, INTRIVITY_LOAD_GROUP_FROM_STORAGE,
    INTRIVITY_LOADED_GROUP, isFlagOn
} from './utility.js'
import { t, Localized, formatDate, getLocale, LOCALES, resolveLocale } from './i18n.js';

// Display names for stored enum values; the stored values stay English.
const statusLabel = (s) => (['pending', 'unsent', 'sent', 'claimed'].includes(s) ? t(`invitations.status.${s}`) : s);
const ROLE_KEYS = { user: 'role.user', trainer: 'role.trainer', program_manager: 'role.programManager', admin: 'role.admin' };

// Import necessary MWC components
import '@material/mwc-button';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-list/mwc-list.js';
import '@material/mwc-list/mwc-list-item.js';
import '@material/mwc-snackbar';

export class IntrivityAdminInvitations extends Localized(LitElement) {
  static get properties() {
    return {
      groupUuid: { type: String },
      user_profile: { type: Object },
      invitations: { type: Array },
      _invitationGroup: { type: Object },
      _isDialogOpen: { type: Boolean },
      _editingInvitation: { type: Object },
      _invitationFormData: { type: Object },
      _isLoading: { type: Boolean },
      _csvUpload: { type: Boolean },
      _errorMessage: { type: String },
      _feedbackMessage: { type: String },
      _users: { type: Array},
      _mailResults: { type: Array },
      _mailDialogOpen: { type: Boolean },
      _mailViewOpen: { type: Boolean },
      _mailViewHtml: { type: String },
      _mailLoading: { type: Boolean },
      _paymentRequired: { type: Boolean },
      _paidByEmail: { type: Object },
      _reportByEmail: { type: Object },
      _dueDate: { type: String },
      mode: { type: String }
    };
  }

  constructor() {
    super();
    this.invitations = [];
    this._isDialogOpen = false;
    this._editingInvitation = null;
    this._invitationFormData = this._getEmptyInvitationData();
    this.loadRequestedGroup = this.loadRequestedGroup.bind(this);
    this._isLoading = false;
    this._errorMessage = '';
    this._feedbackMessage = '';
    this._mailResults = [];
    this._mailDialogOpen = false;
    this._mailViewOpen = false;
    this._mailViewHtml = '';
    this._mailLoading = false;
    this._paymentRequired = false;
    this._paidByEmail = {};
    this._reportByEmail = {};
    this._dueDate = null;
    this.mode = 'client';
  }

  connectedCallback() {
      super.connectedCallback();
      document.addEventListener(INTRIVITY_LOADED_GROUP, this.loadRequestedGroup); 
  }
  
  disconnectedCallback() {
      super.connectedCallback();
      document.addEventListener(INTRIVITY_LOADED_GROUP, this.loadRequestedGroup); 
  }  
  
  _getEmptyInvitationData() {
        let email = this.user_profile ? this.user_profile.email: '';
        if (this.mode == 'trainer') {   
            return {
              uuid: '',
              email: '',
              first: '',
              last: '',
              claimed_by: '',
              cc: 'No',
              group_uuid: this.groupUuid || '',
              code: this._generateRandomCode(),
              status: 'pending',
              trainer: email,
              created_by: email,
            };
        } else {
            return {
              uuid: '',
              email: '',
              first: '',
              last: '',
              claimed_by: '',
              cc: 'No',
              group_uuid: this.groupUuid || '',
              code: this._generateRandomCode(),
              status: 'pending',
              created_by: email,
            };  
        }
  }

  async _loadInvitations() {
    if (!this.groupUuid) return;
    const groupRef = doc(db, 'groups', this.groupUuid);
    const invitationsRef = collection(groupRef, 'invitations');
    const snapshot = await getDocs(invitationsRef);
    this.invitations = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    // Re-check report availability against the freshly loaded group report.
    this._reportByEmail = {};
    emitEvent(INTRIVITY_LOAD_GROUP_FROM_STORAGE, { group_uuid: this.groupUuid }, document);
    await this._loadPaymentState(groupRef);
  }

  // Whether this group charges a fee, and which claimants have paid. Both are
  // read here rather than in render() so the row markup stays synchronous.
  //
  // payment_required round-trips through an mwc-select like the other toggle
  // fields, so "off" is stored as the string '0' -- truthy under plain
  // coercion. isFlagOn is the shared guard against exactly that.
  async _loadPaymentState(groupRef) {
    try {
      const groupSnap = await getDoc(groupRef);
      this._paymentRequired = groupSnap.exists()
        ? isFlagOn(groupSnap.data().payment_required)
        : false;
    } catch (e) {
      // Fail open: if the group cannot be read, do not mislabel people as unpaid.
      console.warn('Could not read the group payment setting:', e);
      this._paymentRequired = false;
      return;
    }

    if (!this._paymentRequired) {
      this._paidByEmail = {};
      return;
    }

    const emails = [...new Set(
      this.invitations
        .filter((inv) => inv.status === 'claimed' && inv.claimed_by)
        .map((inv) => inv.claimed_by)
    )];

    const paid = {};
    await Promise.all(emails.map(async (userEmail) => {
      try {
        const userSnap = await getDoc(doc(db, 'users', userEmail));
        paid[userEmail] = userSnap.exists() && userSnap.data().paid === true;
      } catch (e) {
        // Unknown rather than unpaid -- see _isUnpaid.
        console.warn(`Could not read payment status for ${userEmail}:`, e);
      }
    }));
    this._paidByEmail = paid;
  }

  // Only true when we positively know the fee applies and this person has not
  // paid. Three cases end up false rather than unpaid:
  //   - the group charges no fee
  //   - the invitation is for a trainer, who runs the group rather than
  //     taking a paid assessment (the button below already special-cases them)
  //   - the user document could not be read at all, so the email is absent
  //     from the map and stays undefined -- better to show the normal button
  //     than to tell an admin someone is unpaid on the strength of a failed read
  // A user document that exists without paid:true is genuinely unpaid, which is
  // the same reading the rest of the app takes of that field.
  _isUnpaid(invitation) {
    if (!this._paymentRequired) return false;
    if (!invitation || !invitation.claimed_by) return false;
    if (invitation.role === 'trainer') return false;
    return this._paidByEmail[invitation.claimed_by] === false;
  }
 
    async loadRequestedGroup(e) {
        this._invitationGroup = e.detail;
        await this._loadMissingReports();
    }

  // The group report only lists people whose report was folded into it the last
  // time it was built, so someone who joined afterwards can have a perfectly good
  // personal report and still be absent from it. For each claimed invitation the
  // group report does not cover, ask Storage whether reports/{email}.json exists
  // -- the same file the masquerade opens. Only people missing from the group
  // report are checked, so a fully up-to-date group costs nothing.
  //
  // When the group (or its parent) has a due date, the report is also read for
  // its date_modified, because a response that arrived after the due date is
  // deliberately left out of the group report (functions/groups.js) and the
  // panel says so. Without a due date, existence is all that is needed.
  async _loadMissingReports() {
    const emails = [...new Set(
      this.invitations
        .filter((inv) => inv.status === 'claimed' && inv.claimed_by && inv.role !== 'trainer')
        .map((inv) => inv.claimed_by)
        .filter((email) => !this._getUser(email) && !(email in this._reportByEmail))
    )];
    if (!emails.length) return;

    const due = await this._loadEffectiveDue();
    this._dueDate = due;

    const found = {};
    await Promise.all(emails.map(async (email) => {
      try {
        const url = await getDownloadURL(ref(storage, `reports/${email}.json`));
        let modified = null;
        if (due) {
          const response = await fetch(url);
          if (response.ok) {
            const report = await response.json();
            modified = report.date_modified || null;
          }
        }
        found[email] = { exists: true, modified };
      } catch (err) {
        if (err && err.code === 'storage/object-not-found') {
          found[email] = { exists: false, modified: null };
        } else {
          // Unknown rather than absent: leave it out of the map so a later
          // group load tries again, and the button falls back to "No Report Yet".
          console.warn(`Could not check for a report for ${email}:`, err);
        }
      }
    }));
    this._reportByEmail = { ...this._reportByEmail, ...found };
  }

  // The due date that applies to this group: its own, or its parent's when it
  // has none (a team inherits the group it belongs to), same as
  // processOneGroupReport. Null when neither is set or the read fails.
  async _loadEffectiveDue() {
    try {
      const groupSnap = await getDoc(doc(db, 'groups', this.groupUuid));
      if (!groupSnap.exists()) return null;
      const group = groupSnap.data();
      if (group.due) return group.due;
      if (group.parent) {
        const parentSnap = await getDoc(doc(db, 'groups', group.parent));
        if (parentSnap.exists() && parentSnap.data().due) return parentSnap.data().due;
      }
    } catch (e) {
      console.warn('Could not read the group due date:', e);
    }
    return null;
  }

  _hasReport(email) {
    const known = this._reportByEmail[email];
    return !!(known && known.exists);
  }

  // The due date is a datetime-local string with no zone, and the server reads it
  // with new Date() in Cloud Functions, which run in UTC. Parse it the same way
  // here so the panel agrees with what the server actually did, whatever the
  // browser's own timezone.
  _dueAsServerSees(due) {
    return new Date(/(Z|[+-]\d\d:?\d\d)$/.test(due) ? due : `${due}Z`);
  }

  // Why a claimed person is not in the group report, or null when they are (or
  // when it cannot be said -- trainers are left out by design, and with no group
  // report loaded everyone would be flagged, which tells the reader nothing).
  _groupReportNote(invitation) {
    if (invitation.status !== 'claimed' || !invitation.claimed_by) return null;
    if (invitation.role === 'trainer') return null;
    if (!this._invitationGroup || !this._invitationGroup.users) return null;
    if (this._getUser(invitation.claimed_by)) return null;

    const known = this._reportByEmail[invitation.claimed_by];
    if (known && !known.exists) return t('invitations.notInReportNoReport');
    if (known && known.modified && this._dueDate) {
      const modified = new Date(known.modified);
      const due = this._dueAsServerSees(this._dueDate);
      if (!isNaN(modified) && !isNaN(due) && modified > due) {
        return t('invitations.notInReportLate', { taken: formatDate(modified, { dateStyle: 'medium', timeStyle: 'short' }), due: formatDate(due, { dateStyle: 'medium', timeStyle: 'short' }) });
      }
    }
    return t('invitations.notInReport');
  }

  /**
   * Query the 'mail' collection for messages sent to the invitation email.
   * Shows the results in a dialog.
   */
  async _checkMail(invitation) {
    if (!invitation || !invitation.email) {
      this._showFeedback(t('invitations.noEmail'));
      return;
    }

    this._mailLoading = true;
    this._mailResults = [];
    this._mailDialogOpen = true;

    try {
      const mailRef = collection(db, 'mail');
      const q = query(mailRef, where('to', '==', invitation.email));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        this._mailResults = [];
        this._showFeedback(t('invitations.noMailFor', { email: invitation.email }));
      } else {
        // Map docs to a friendly object
        this._mailResults = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            to: data.to,
            subject: (data.message && data.message.subject) || '',
            html: (data.message && data.message.html) || (data.message && data.message.body) || '',
            delivery: data.delivery || {},
          };
        }).sort((a,b) => {
          // sort descending by startTime if present
          const ta = a.delivery && a.delivery.startTime ? this._toEpochMillis(a.delivery.startTime) : 0;
          const tb = b.delivery && b.delivery.startTime ? this._toEpochMillis(b.delivery.startTime) : 0;
          return tb - ta;
        });
      }
    } catch (err) {
      console.error('Error querying mail collection', err);
      this._showFeedback(t('invitations.mailQueryFailed'));
    } finally {
      this._mailLoading = false;
    }
  }


  _closeMailDialog() {
    this._mailDialogOpen = false;
    this._mailResults = [];
  }

  _openMailView(mailHtml) {
    // mailHtml is HTML string. We store it and open the view dialog
    this._mailViewHtml = mailHtml || '';
    this._mailViewOpen = true;
  }

  _closeMailView() {
    this._mailViewOpen = false;
    this._mailViewHtml = '';
  }

  _getUser(email) {
      if (this._invitationGroup && this._invitationGroup.users) {
          for (var i=0; i < this._invitationGroup.users.length; i++) {
              if (this._invitationGroup.users[i].email === email) {
                  return this._invitationGroup.users[i];
              }
          }
      }  
  }
/**
 * Normalize Firestore Timestamp / Date / number / string into epoch milliseconds
 */
_toEpochMillis(ts) {
  try {
    if (!ts) return null;

    let date;

    // Firestore Timestamp (seconds / nanoseconds)
    if (typeof ts === 'object' && ts.seconds !== undefined) {
      const ms = ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
      return ms;
    }

    // Firestore Timestamp (client SDK: toMillis / toDate)
    if (typeof ts === 'object') {
      if (typeof ts.toMillis === 'function') {
        return ts.toMillis();
      }
      if (typeof ts.toDate === 'function') {
        date = ts.toDate();
      }
    }

    // JS Date
    if (ts instanceof Date) {
      date = ts;
    }
    // number (epoch ms)
    else if (typeof ts === 'number') {
      date = new Date(ts);
    }
    // string (ISO, human-readable, etc.)
    else if (typeof ts === 'string') {
      let s = ts.trim();

      // normalize invisible spaces
      s = s.replace(/\u202F|\u00A0/g, ' ');

      // remove " at " (e.g. "December 12, 2024 at 9:24:06 AM UTC-5")
      s = s.replace(/\bat\b/i, ' ');

      // normalize "UTC-5" / "UTC+2" → "GMT-0500" / "GMT+0200"
      s = s.replace(/\bUTC([+-]\d{1,2})\b/i, (_, off) => {
        const hours = Math.abs(Number(off));
        const sign = off.startsWith('-') ? '-' : '+';
        return `GMT${sign}${String(hours).padStart(2, '0')}00`;
      });

      date = new Date(s);
    }

    if (!date || isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  } catch (e) {
    return null;
  }
}


  /**
   * Format Firestore Timestamp / Date / string into human-readable date+time
   */
  _formatDate(ts) {
    try {
      if (!ts) return '—';

      let date;

      // Firestore Timestamp (most important case)
      if (typeof ts === 'object' && ts.seconds !== undefined) {
        date = new Date(ts.seconds * 1000);
      }
      // JS Date
      else if (ts instanceof Date) {
        date = ts;
      }
      // number (epoch ms)
      else if (typeof ts === 'number') {
        date = new Date(ts);
      }
      // string (ISO or other)
      else if (typeof ts === 'string') {
        date = new Date(ts);
      }

      if (!date || isNaN(date.getTime())) {
        return t('common.invalidDate');
      }

      return date.toLocaleString(getLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (e) {
      return '—';
    }
  }

    _generateRandomCode() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      const length = 8;
      const array = new Uint32Array(length);
      crypto.getRandomValues(array);

      return Array.from(array, x => chars[x % chars.length]).join('');
    }

  _openAddDialog() {
    this._editingInvitation = null;
    this._invitationFormData = this._getEmptyInvitationData();
    this._isDialogOpen = true;
  }

  _openEditDialog(invitation) {
    this._editingInvitation = invitation;
    this._invitationFormData = { ...invitation };
    this._isDialogOpen = true;
  }

  _closeDialog() {
    this._isDialogOpen = false;
    this._invitationFormData = this._getEmptyInvitationData();
  }

  _handleFormInput(e) {
    const target = e.target;
    let { name, value } = target;

    if (!name) {
      name = target.dataset.name;
    }

    if (value === 'undefined') value = undefined;

    // Trim whitespace if value is a string
    if (typeof value === 'string') {
      value = value.trim();
    }

    this._invitationFormData = { ...this._invitationFormData, [name]: value };
  }

    async ensureRole(invitation) {
      const q = query(
        collection(db, "group_roles"),
        where("email", "==", invitation.claimed_by),
        where("group_uuid", "==", invitation.group_uuid)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // No document exists — create a new one
        await addDoc(collection(db, "group_roles"), {
          email: invitation.claimed_by,
          group_uuid: invitation.group_uuid,
          role: invitation.role,
          created_at: new Date().toISOString()
        });
      } else {
        // Document exists — check and update roles if needed
        const docRef = snapshot.docs[0].ref;
        const data = snapshot.docs[0].data();
        const roles = data.role || [];

        if (!roles.includes(invitation.role)) {
          await updateDoc(docRef, {
            role: [...roles, invitation.role],
            updated_at: new Date().toISOString()
          });
        } else {
        }
      }
    }


  async _saveInvitation() {
    const invitation = { ...this._invitationFormData };
    if (!invitation.uuid) invitation.uuid = invitation.email; // crypto.randomUUID();
    const groupRef = doc(db, 'groups', this.groupUuid);
    const invitationRef = doc(groupRef, 'invitations', invitation.uuid);
console.log(invitation);
    await setDoc(invitationRef, invitation);
    if (invitation.role && invitation.role !== 'user') {
        if (invitation.claimed_by && invitation.claimed_by.trim().length > 3) {
            this.ensureRole(invitation);
        }
    }
    this._showFeedback(t('invitations.saved', { email: invitation.email }));
    this._closeDialog();
    this._loadInvitations();
  }

    async readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error);
        reader.onload = () => resolve(reader.result);
        reader.readAsText(file);
      });
    }
 
 
    simpleCsvParse(text) {
      // Expected columns: email, first name, last name -- with an optional
      // header row, which may also name a "language" column for the
      // invitation email (en, es, fr, zh-hans, zh-hant, or tags like zh-TW). Minimal parser: no quoted fields containing commas.
      const lines = text
        .replace(/^\uFEFF/, '')               // Excel's UTF-8 byte-order mark
        .split(/\r\n|\n|\r/)
        .filter(l => l.trim().length > 0);
      if (!lines.length) return [];

      // A header row is one with no email address in it. If present, use it
      // to locate the columns; otherwise assume email, first, last.
      let cols = { email: 0, first: 1, last: 2 };
      let startIdx = 0;
      if (!lines[0].includes('@')) {
        startIdx = 1;
        const names = lines[0].split(',').map(s => s.trim().toLowerCase());
        const find = (test, fallback) => {
          const i = names.findIndex(test);
          return i >= 0 ? i : fallback;
        };
        cols = {
          email: find(n => n.includes('mail'), 0),
          first: find(n => n.includes('first'), 1),
          last:  find(n => n.includes('last'), 2),
          language: find(n => n.includes('lang'), -1),
        };
      }

      const rows = [];
      for (let i = startIdx; i < lines.length; i++) {
        const parts = lines[i].split(',').map(s => s.trim());
        rows.push({
          line: i + 1,
          email: parts[cols.email] || '',
          first: parts[cols.first] || '',
          last:  parts[cols.last]  || '',
          language: cols.language >= 0 ? (parts[cols.language] || '').toLowerCase() : '',
        });
      }
      return rows;
    }

    isValidEmail(email) {
      if (!email) return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

  async _onUploadCsvClick() {
    const input = this.shadowRoot.getElementById('inviteCsvInput');
    const file = input && input.files && input.files[0];
    if (!file) {
      this._showFeedback(t('invitations.chooseCsv'));
      return;
    }
    await this._importInvitationsFromCsvFile(file);
    this._csvUpload = false;
  }

  async _importInvitationsFromCsvFile(file) {
    try {
      if (!this.groupUuid) {
        this._showFeedback(t('invitations.noGroup'));
        return;
      }
      const rows = this.simpleCsvParse(await this.readFileAsText(file));
      if (!rows.length) {
        this._showFeedback(t('invitations.noRows'));
        return;
      }

      // Never overwrite an existing invitation: that would reset a claimed
      // one back to pending and re-send its email.
      await this._loadInvitations();
      const existing = new Set(
        this.invitations.map(i => (i.email || i.uuid || '').trim().toLowerCase())
      );

      const seen = new Set();
      const toSave = [];
      const errors = [];
      const skipped = [];
      for (const r of rows) {
        const email = r.email.toLowerCase();
        if (!this.isValidEmail(email)) {
          errors.push(t('invitations.csvInvalidEmail', { line: r.line, email: r.email }));
          continue;
        }
        const language = r.language ? resolveLocale(r.language) : '';
        if (r.language && !language) {
          errors.push(t('invitationLanguage.csvUnknown', { line: r.line, language: r.language, known: Object.keys(LOCALES).join(', ') }));
          continue;
        }
        if (seen.has(email) || existing.has(email)) {
          skipped.push(email);
          continue;
        }
        seen.add(email);
        toSave.push({
          ...this._getEmptyInvitationData(),
          uuid: email,
          email,
          first: r.first,
          last: r.last,
          ...(language ? { language } : {}),
          group_uuid: this.groupUuid,
        });
      }

      if (toSave.length) {
        const groupRef = doc(db, 'groups', this.groupUuid);
        for (let i = 0; i < toSave.length; i += 500) {
          const batch = writeBatch(db);
          for (const inv of toSave.slice(i, i + 500)) {
            batch.set(doc(groupRef, 'invitations', inv.uuid), inv);
          }
          await batch.commit();
        }
        await this._loadInvitations();
      }

      let msg = t('invitations.csvImported', { count: toSave.length });
      if (skipped.length) msg += '\n' + t('invitations.csvSkipped', { count: skipped.length, list: skipped.join(', ') });
      if (errors.length) msg += '\n' + t('invitations.csvRejected', { count: errors.length }) + '\n' + errors.join('\n');
      this._showFeedback(msg);
    } catch (err) {
      console.error(err);
      this._showFeedback(t('invitations.csvFailed', { message: err.message || err }));
    }
  }

  async _deleteInvitation(invitation) {
    if (confirm(t('invitations.confirmDelete', { email: invitation.email }))) {
      const groupRef = doc(db, 'groups', this.groupUuid);
      await deleteDoc(doc(groupRef, 'invitations', invitation.uuid));
      this._showFeedback(t('invitations.deleted', { email: invitation.email }));
      this._loadInvitations();
    }
  }

  _showFeedback(message) {
    this._feedbackMessage = message;
    alert(message);
//    this.shadowRoot.getElementById('feedback-snackbar').open();
  }

  static get styles() {
    return css`
      .invitation-item {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr auto;
      }
      .actions mwc-button {
        margin-left: 5px;
      }
      .invitation-name {
        font-weight: bold;
      }
      .invitation-email,
      .invitation-cc,
      .invitation-code,
      .invitation-status {
        font-size: 0.85em;
        color: #6b6b6b;
      }
      .invitation-report-note {
        font-size: 0.85em;
        color: #b45309;
      }
      .invitation-status.claimed, 
      .invitation-email.claimed,
      .invitation-name.claimed {
        color: green;          /* or use a custom CSS var */
        font-weight: 600;
      }
    `;
  }

  firstUpdated() {
    this._loadInvitations();
  }
  
  updated(changed) {
    if (changed.has('_mailViewOpen') && this._mailViewOpen) {
      // Put the HTML into the container (danger: untrusted HTML).
      const el = this.shadowRoot && this.shadowRoot.getElementById('mailBodyContainer');
      if (el) {
        // Insert HTML. If you need sanitization, perform it here before assigning.
        el.innerHTML = this._mailViewHtml || '<div>(no content)</div>';
      }
    }
  }


  // if user is a program_manager for group, show ALL
  isAvailable(invite) {
    if (this.mode === 'trainer') {
        if (invite.hasOwnProperty("trainer")) {
            return invite.trainer === this.user_profile.email;
        } else {
            return false;
        }
    } else {
        return true;
    }
      
  }
  
  render() {
    return html`
      <p style="font: smaller;">
        ${t('invitations.help1')}<br />
        ${t('invitations.help2')}<br />
        ${t('invitations.help3')}<br />
        ${t('invitations.help4')}
      </p>
      ${hasRole('superadmin', this.user_profile) || hasRole('program_manager', this.user_profile) || hasRole('client_manager', this.user_profile) || this.mode == 'trainer' ? html`<mwc-button
        raised
        label=${t('invitations.add')}
        @click=${this._openAddDialog}
      ></mwc-button>`: html``}
      ${hasRole('superadmin', this.user_profile) && !this._csvUpload ? html`<mwc-button
        raised
        label=${t('invitations.addCsv')}
        @click=${(e) => { this._csvUpload = true; }}
      ></mwc-button>`: html``}
        
        ${hasRole('superadmin', this.user_profile) && this._csvUpload ? html`
            <span>
                <label for="inviteCsvInput">${t('invitations.csvFile')}</label>
                <input type="file" id="inviteCsvInput" accept=".csv" />
                <button type="button" id="uploadCsvBtn" @click=${this._onUploadCsvClick}>${t('invitations.uploadCsv')}</button>
                <button type="button" @click=${() => { this._csvUpload = false; }}>${t('common.cancel')}</button>
            </span>
        `: html``}
        
      <mwc-list>
        ${this.invitations.sort((a, b) => a.last.localeCompare(b.last) || a.first.localeCompare(b.first)).map(
          (invitation, n) => html`
            ${this.isAvailable(invitation) ? html`
            <mwc-list-item style="display: block; width: auto; height: auto;">
              <div class="invitation-item">
                <div style="min-width: 0;">
                  <div class="invitation-name ${invitation.status === 'claimed' ? 'claimed' : ''}">
                    ${invitation.first} ${invitation.last}
                    ${invitation.role && invitation.role == 'trainer' ? html`
                        <span style="color: black; font-size: small;">
                            (${t('role.trainer')})
                        </span>
                    `: html``}
                  </div>
                  <div class="invitation-email ${invitation.status === 'claimed' ? 'claimed' : ''}">
                        ${invitation.status === 'claimed' ? invitation.claimed_by : invitation.email}
                  </div>
                  <div class="invitation-status ${invitation.status === 'claimed' ? 'claimed' : ''}">
                    ${t('invitations.statusLine', { status: statusLabel(invitation.status) })}
                  </div>
                  ${invitation.language && LOCALES[invitation.language] ? html`
                    <div class="invitation-status">${t('invitationLanguage.line', { language: LOCALES[invitation.language].name })}</div>
                  ` : html``}
                  ${this._groupReportNote(invitation) ? html`
                    <div class="invitation-report-note">${this._groupReportNote(invitation)}</div>
                  ` : html``}
                  ${invitation.status === 'claimed' ? html`
                    <div class="invitation-code">
                      ${t('invitations.joinCode', { code: invitation.code || t('invitations.noCode') })}
                    </div>
                  `: html``}
                  <div class="invitation-uuid" style="font-size: 0.7em; color: #6b6b6b;">ID: ${invitation.uuid}</div>
                  ${invitation.trainer ? html`
                        <div class="invitation-uuid" style="font-size: 0.7em; color: #6b6b6b;">${t('invitations.trainerLine', { email: invitation.trainer })}</div>
                   `: html``}
                </div>
                <div class="actions">
        
                  ${!invitation.status || invitation.status !== 'claimed' ? html`<mwc-button
                    outlined
                    label=${t('invitations.checkMail')}
                    @click=${() => this._checkMail(invitation)}
                  ></mwc-button>`: html``}       
        
                  ${(hasRole('superadmin', this.user_profile) || this.mode == 'trainer' || hasRole('program_manager', this.user_profile) || hasRole('client_manager', this.user_profile)) && invitation.status === 'claimed' ? html`
                        ${this._isUnpaid(invitation) ? html`
                            <mwc-button
                                outlined
                                label=${t('invitations.unpaid')}
                                disabled
                                title=${t('invitations.unpaidTitle')}
                            ></mwc-button>
                        `: html`
                        ${(invitation.role && invitation.role == 'trainer') || this._getUser(invitation.claimed_by) || this._hasReport(invitation.claimed_by) ? html`
                            <mwc-button
                                outlined
                                label=${t('invitations.seeReport')}
                                @click=${() => emitEvent(INTRIVITY_MASQUERADE,{ email: invitation.claimed_by },this)}
                            ></mwc-button>           
                        `: html`
                            <mwc-button
                                outlined
                                label=${t('invitations.noReportYet')}
				disabled
                            ></mwc-button>                           
                        `}
                        `}
                    `: html``}
                  ${hasRole('superadmin', this.user_profile) || ((this.mode == 'trainer' ||  hasRole('program_manager', this.user_profile) || hasRole('client_manager', this.user_profile)) && invitation.status !== 'claimed') ? html`<mwc-button
                    outlined
                    label=${t('common.edit')}
                    @click=${() => this._openEditDialog(invitation)}
                  ></mwc-button>`: html``}
                  ${hasRole('superadmin', this.user_profile) || (this.mode == 'trainer' && invitation.status !== 'claimed') ? html`<mwc-button
                    outlined
                    label=${t('common.delete')}
                    @click=${() => this._deleteInvitation(invitation)}
                  ></mwc-button>`: html``}
                </div>
              </div>
              ${n < this.invitations.length ? html`
                <hr>
              `: html``}
              `: html``}
            </mwc-list-item>
          `
        )}
      </mwc-list>

      <mwc-dialog
        heading="${this._editingInvitation ? t('invitations.edit') : t('invitations.add')}"
        ?open=${this._isDialogOpen}
        @closed=${this._closeDialog}
      >
        <mwc-textfield style="width: 100%;"
          label=${t('login.email')}
          name="email"
          .value=${this._invitationFormData.email}
          @input=${this._handleFormInput}
        ></mwc-textfield>
        <div style="width: 100%; display: grid; grid-template-columns: 1fr 1fr;">
            <mwc-textfield
              label=${t('common.firstName')}
              name="first"
              .value=${this._invitationFormData.first}
              @input=${this._handleFormInput}
            ></mwc-textfield>
            <mwc-textfield
              label=${t('common.lastName')}
              name="last"
              .value=${this._invitationFormData.last}
              @input=${this._handleFormInput}
            ></mwc-textfield>
            <mwc-select
              label=${t('invitations.statusLabel')}
              name="status"
              data-name="status"
              .value=${this._invitationFormData.status}
              @selected=${this._handleFormInput}
              @closed=${e => e.stopPropagation()}
              style="width:100%;"
            >
              ${['pending', 'unsent', 'sent', 'claimed'].map(
                s => html`<mwc-list-item value=${s}>${statusLabel(s)}</mwc-list-item>`)}
            </mwc-select>
            <mwc-textfield
              label=${t('invitations.code')}
              name="code"
              .value=${this._invitationFormData.code}
              @input=${this._handleFormInput}
            ></mwc-textfield>
        </div>
        <mwc-select
          label=${t('invitations.cc')}
          name="cc"
          data-name="cc"
          .value=${this._invitationFormData.cc}
          @selected=${this._handleFormInput}
          @closed=${e => e.stopPropagation()}
          style="width:100%;"
        >
          ${['Yes', 'No'].map(
            s => html`<mwc-list-item value=${s}>${s === 'Yes' ? t('common.yes') : t('common.no')}</mwc-list-item>`)}
        </mwc-select>
        <label style="display: block; margin: 8px 0;">
          ${t('invitationLanguage.label')}
          <select @change=${(e) => { this._invitationFormData = { ...this._invitationFormData, language: e.target.value }; }}>
            <option value="" ?selected=${!this._invitationFormData.language}>${t('invitationLanguage.groupDefault')}</option>
            ${Object.keys(LOCALES).map((code) => html`
              <option value=${code} ?selected=${this._invitationFormData.language === code}>${LOCALES[code].name}</option>
            `)}
          </select>
        </label>
        <mwc-textfield style="width: 100%;"
          label=${t('invitations.claimedBy')}
          name="claimed_by"
          .value=${this._invitationFormData.claimed_by}
          @input=${this._handleFormInput}
        ></mwc-textfield>
        ${hasRole('superadmin', this.user_profile) || hasRole('program_manager', this.user_profile) || hasRole('client_manager', this.user_profile) ? html`
            <mwc-textfield style="width: 100%;"
              label=${t('invitations.trainerEmail')}
              name="trainer"
              helper=${t('invitations.trainerHelper')}
              .value=${this._invitationFormData.trainer || ''}
              @input=${this._handleFormInput}
            ></mwc-textfield>
        `: html``}
        ${hasRole('superadmin', this.user_profile) ? html`
            <mwc-select
                  label=${t('roleGuide.colRole')}
                  name="role"
                  data-name="role"
                  .value=${this._invitationFormData.role}
                  @selected=${this._handleFormInput}
                  @closed=${e => e.stopPropagation()}
                  style="width:100%;"
                >
                  ${['user', 'trainer', 'program_manager', 'admin'].map(
                    s => html`<mwc-list-item value=${s}>${t(ROLE_KEYS[s])}</mwc-list-item>`)}
            </mwc-select>
        `: html``}

        <mwc-button
          slot="primaryAction"
          label=${t('common.save')}
          @click=${this._saveInvitation}
        ></mwc-button>
        <mwc-button
          slot="secondaryAction"
          dialogAction="cancel"
          label=${t('common.cancel')}
        ></mwc-button>
      </mwc-dialog>
        
      <!-- Mail list dialog -->
      <mwc-dialog
        heading=${t('invitations.mailHeading')}
        ?open=${this._mailDialogOpen}
        @closed=${this._closeMailDialog}
      >
        ${this._mailLoading ? html`<div>${t('common.loading')}</div>` : html`
          ${this._mailResults && this._mailResults.length ? html`
            <div style="max-height:60vh; overflow:auto; padding: 8px;">
              ${this._mailResults.map(mail => html`
                <div style="border:1px solid #eee; padding:8px; margin-bottom:8px;">
                  <div style="display:flex; justify-content:space-between; align-items:center;">
                    <div>
                      <div style="font-weight:600;">${mail.subject || t('invitations.noSubject')}</div>
                      <div style="font-size:0.85em; color:#666;">${t('invitations.to', { email: mail.to })}</div>
                    </div>
                    <div>
                      <mwc-button dense outlined label=${t('invitations.viewMessage')} @click=${() => this._openMailView(mail.html)}></mwc-button>
                    </div>
                  </div>

                  <div style="margin-top:6px; font-size:0.85em; color:#444;">
                    <div>${t('invitations.deliveryState', { state: mail.delivery && mail.delivery.state ? mail.delivery.state : '—' })}</div>
                    <div>${t('invitations.start', { date: this._formatDate(mail.delivery && mail.delivery.startTime) })}</div>
                    <div>${t('invitations.end', { date: this._formatDate(mail.delivery && mail.delivery.endTime) })}</div>
                    ${mail.delivery && mail.delivery.error ? html`<div style="color:crimson;">${t('invitations.error', { message: mail.delivery.error })}</div>` : html``}
                    ${mail.delivery && mail.delivery.info ? html`
                      <div style="font-size:0.85em; color:#666; margin-top:4px;">
                        <div>${t('invitations.accepted', { value: mail.delivery.info.accepted || 0 })}</div>
                        <div>${t('invitations.rejected', { value: mail.delivery.info.rejected || 0 })}</div>
                        <div>${t('invitations.pending', { value: mail.delivery.info.pending || 0 })}</div>
                      </div>
                    `: html``}
                    <div style="font-size:0.75em; color:#6b6b6b; margin-top:6px;">ID: ${mail.id}</div>
                  </div>
                </div>
              `)}
            </div>
          ` : html`<div>${t('invitations.noMail')}</div>`}
        `}
        <mwc-button slot="primaryAction" dialogAction="close" label=${t('common.close')}></mwc-button>
      </mwc-dialog>

      <!-- Mail view dialog (renders HTML) -->
      <mwc-dialog
        heading=${t('invitations.mailMessage')}
        ?open=${this._mailViewOpen}
        @closed=${this._closeMailView}
      >
        <div style="max-height:70vh; overflow:auto; padding:8px;">
          <!-- CAUTION: we are inserting raw HTML from the mail record.
               Consider sanitizing before injecting to avoid XSS in admin UI. -->
          <div id="mailBodyContainer" style="min-height:200px;"></div>
        </div>
        <mwc-button slot="primaryAction" dialogAction="close" label=${t('common.close')}></mwc-button>
      </mwc-dialog>
        

      <mwc-snackbar
        id="feedback-snackbar"
        labelText=${this._feedbackMessage}
      ></mwc-snackbar>
    `;
  }
}

customElements.define('intrivity-admin-invitations', IntrivityAdminInvitations);
